import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../../lib/supabase";
import BannerNavRow from "./Menus/BannerNavRow";

type BannerSlide = {
  id: number;
  brandId: number;
  brandName: string;
  title: string;
  subtitle: string | null;
  ctaLabel: string;
  imageUrl: string;
};

const FILE_ID = "✅ REAL BannerSlideshow RUNNING";

const BannerSlideshow = () => {
  const [slides, setSlides] = useState<BannerSlide[]>([]);
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    console.log(FILE_ID);
  }, []);

  useEffect(() => {
    let mounted = true;

    const fetchSlides = async () => {
      console.log("🔄 Fetching slides from Supabase...");

      const { data, error } = await supabase
        .from("BannerSlide")
        .select(
          `
          id,
          brand_id,
          title,
          subtitle,
          cta_label,
          image_url,
          sort_order,
          is_active,
          Brand ( id, name )
        `
        )
        .eq("is_active", true)
        .order("sort_order", { ascending: true });

      if (!mounted) return;

      if (error) {
        console.error("❌ Supabase error:", error.message);
        setSlides([]);
        return;
      }

      console.log("✅ Slides returned:", data?.length ?? 0);

      const mapped: BannerSlide[] = (data || [])
        .filter((row: any) => !!row.image_url)
        .map((row: any) => ({
          id: row.id,
          brandId: row.brand_id,
          brandName: row.Brand?.name ?? "Brand",
          title: row.title,
          subtitle: row.subtitle,
          ctaLabel: row.cta_label || "Shop Now",
          imageUrl: row.image_url,
        }));

      setSlides(mapped);
      setCurrent(0);
    };

    fetchSlides();

    return () => {
      mounted = false;
    };
  }, []);

  const totalSlides = slides.length;

  useEffect(() => {
    if (totalSlides === 0) return;
    if (current > totalSlides - 1) setCurrent(0);
  }, [totalSlides, current]);

  useEffect(() => {
    if (totalSlides <= 1) return;

    const interval = window.setInterval(() => {
      setCurrent((prev) => (prev + 1) % totalSlides);
    }, 5000);

    return () => window.clearInterval(interval);
  }, [totalSlides]);

  const slide = useMemo(() => slides[current], [slides, current]);

  if (!slide) {
    return (
      <div className="h-[70vh] flex items-center justify-center bg-[#fbe7e7] rounded-lg">
        <p className="text-[#5c2f2f] font-semibold">Loading banner...</p>
      </div>
    );
  }

  return (
    <div className="relative min-h-[70vh] w-full rounded-lg overflow-hidden">
      {/* Background */}
      <div
        className="absolute inset-0 bg-cover bg-center transition-all duration-700"
        style={{ backgroundImage: `url(${slide.imageUrl})` }}
      />

      {/* Overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-[#fbe7e7]/70 to-[#f7dada]/70 z-10" />

      {/* Nav */}
      <BannerNavRow />

      {/* Content */}
      <div className="relative z-20 text-center text-[#5c2f2f] px-4 pt-32">
        <h1 className="text-6xl md:text-7xl italic mb-2">
          {slide.title || "Embrace the Glow"}
        </h1>

        {slide.subtitle && <p className="text-lg md:text-xl mb-2">{slide.subtitle}</p>}

        <p className="text-2xl font-bold uppercase mb-4">{slide.brandName}</p>

        <div className="flex flex-col items-center gap-3">
          <Link
            to={`/products?brand=${slide.brandId}`}
            className="inline-flex min-h-[46px] items-center justify-center rounded-full bg-[#a25d5d] px-6 text-sm font-semibold text-white shadow-[0_10px_30px_rgba(92,47,47,0.18)] transition hover:-translate-y-0.5 hover:bg-[#8f4d4d] hover:shadow-[0_14px_36px_rgba(92,47,47,0.25)]"
          >
            {slide.ctaLabel}
          </Link>

          <Link
            to="/products?all=true&ai=true"
            className="group relative inline-flex min-h-[46px] items-center justify-center overflow-hidden rounded-full border border-white/70 bg-white/80 px-5 text-sm font-semibold text-[#5c2f2f] shadow-[0_10px_30px_rgba(92,47,47,0.18)] backdrop-blur transition hover:-translate-y-0.5 hover:bg-white hover:shadow-[0_14px_36px_rgba(92,47,47,0.25)]"
            aria-label="Ask AI to help find products"
          >
            <span className="absolute inset-y-0 -left-10 w-8 rotate-12 bg-white/70 blur-sm transition duration-1000 group-hover:translate-x-64" />
            <span className="absolute left-3 top-1 text-[13px] text-[#d9a0a0] opacity-80 animate-[aiSparkle_1.8s_ease-in-out_infinite]">
              ✦
            </span>
            <span className="absolute right-3 bottom-1 text-[12px] text-[#a25d5d] opacity-75 animate-[aiSparkle_2.2s_ease-in-out_infinite_0.3s] transition duration-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:rotate-45">
              ✦
            </span>
            <span
              className="mr-2 inline-block text-base animate-[aiOrbit_2.8s_ease-in-out_infinite] transition duration-500 group-hover:rotate-180"
              aria-hidden="true"
            >
              ✦
            </span>
            Ask to Find Your Product
          </Link>
        </div>
      </div>

      {/* Dots */}
      {totalSlides > 1 && (
        <div className="absolute bottom-4 w-full flex justify-center gap-2 z-20">
          {slides.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setCurrent(i)}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${
                i === current ? "bg-[#a25d5d] scale-110" : "bg-[#f2cccc]"
              }`}
              aria-label={`Go to slide ${i + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default BannerSlideshow;
